import React from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { usePopulateMemo } from 'api/APIHooks';
import { useFeatureFlag } from 'common/hooks';
import { useCurrentProviderId } from 'common/contexts/CurrentProviderContext';
import { Spinner } from 'common/spinners';
import { AUTHORIZATION_STATUSES } from 'common/Status/authorizationConstants';
import moment from 'moment';
import { ReviewReasonDetailsCard } from 'common/Workqueue/ReviewReasonDetailsCard';
import { getCapInformation } from 'components/ServiceAuthorization/utils';
import { useAssigneeOptions } from 'common/Workqueue/hooks/useAssigneeOptions';
import { useAuthorizedSpendsWithExclusions } from '../hooks/useAuthorizedSpendsWithExclusions';
import { useAuthorizedSpends } from '../hooks/useAuthorizedSpends';
import ServiceAuthorizationDetailsActionBar from '../components/ServiceAuthorizationDetailsActionBar';
import ServiceAuthorizationAcceptedCard from '../components/ServiceAuthorizationAcceptedCard';
import ServiceAuthorizationRejectionCard from '../components/ServiceAuthorizationRejectionCard';
import ServiceAuthorizationVoidedCard from '../components/ServiceAuthorizationVoidedCard';
import AuthorizationDetailsCard from './components/AuthorizationDetailsCard';
import AssessmentFormsCard from '../components/AssessmentFormsCard';
import ServiceAuthorizationTimelineEventsCard from '../components/ServiceAuthorizationTimelineEventsCard';
import CaseDetailsCard from '../components/CaseDetailsCard';
import ClientDetailsCard from '../components/ClientDetailsCard';
import ClientServiceUtilizationCard from '../components/ClientServiceUtilizationCard';
import ReferralDetailsCard from '../components/ReferralDetailsCard';
import UnilaterallyUpdatedAuthorizationCard from './components/UnilaterallyUpdatedAuthorizationCard';
import useDateRequested from '../hooks/useDateRequested';
import { useReviewReasonOptions } from '../hooks/useReviewReasonOptions';
import { useServiceAuthorizationData, usePopulateQueries, useReferralQueries } from '../hooks/useServiceAuthorizationData';
import { useReviewServiceAuthorization } from '../hooks/useUpdateServiceAuthorization';
import { getDisplayDates, getPersonData, hasRequestedOrInReviewEdition } from '../utils/serviceAuthorizationUtils';

const ServiceAuthorizationDetails = ({ params }) => {
  const includeInternalNonConsentedClients = useFeatureFlag('PAYS-7267-include-internal-nonconsent');
  const inReviewFeatureEnabled = useFeatureFlag('pays-8984-auth-assign-in-review');
  const serviceAuthTimelineEnabled = useFeatureFlag('pays-9197-service-authorization-timeline-history-enabled');
  const updateEditAuthorizationsFeatureEnabled = useFeatureFlag('pays-5650-update-edit-authorizations');

  const { isLoading, data } = useServiceAuthorizationData(params, includeInternalNonConsentedClients);

  const authorization = get(data, 'data.data', {});
  const caseId = get(authorization, 'case.id', null);

  const populateQueries = usePopulateQueries(authorization, includeInternalNonConsentedClients);
  const referralQueries = useReferralQueries(caseId);

  const assigneeOptions = useAssigneeOptions('service_authorizations', inReviewFeatureEnabled);
  const reviewReasonOptions = useReviewReasonOptions(
    authorization.fee_schedule_program?.fee_schedule?.id,
    inReviewFeatureEnabled,
  );

  const reviewServiceAuthorization = useReviewServiceAuthorization();

  const handleReviewDetailsSubmit = async (formData) => reviewServiceAuthorization({
    authorization,
    service_authorization_review_reason: formData.review_reason,
    in_review_note: formData.in_review_note,
    assignee: formData.assignee,
  });

  const isMemoLoading = usePopulateMemo(data, [
    populateQueries.fspQuery,
    populateQueries.personQuery,
    populateQueries.caseQuery,
    populateQueries.requesterQuery,
    populateQueries.providerRequesterQuery,
    populateQueries.clinicalModification,
    populateQueries.previousAuthInLineage,
    populateQueries.previousAuthClinicalModifications,
  ], true);
  const isReferralsMemoLoading = usePopulateMemo(referralQueries.referralsData, [
    referralQueries.referralsQuery,
    referralQueries.recNetworkQuery,
    referralQueries.recOrgQuery,
    referralQueries.recProgramQuery,
  ], true);

  const dateRequested = useDateRequested(params.id);
  const { dateRequestedStartsAt, dateRequestedEndsAt, isSingleDateRequested } = getDisplayDates(authorization);
  const feeScheduleProgram = get(authorization, 'fee_schedule_program', {});
  const isFspLoaded = !!feeScheduleProgram?.name;

  const { data: previousAuthData } = populateQueries.previousAuthInLineage;
  const previousAuth = get(previousAuthData, 'data.data', {});
  const showOriginalValues = updateEditAuthorizationsFeatureEnabled &&
    authorization.original_service_authorization &&
    previousAuth &&
    Object.keys(previousAuth).length > 0;

  const currentProviderId = useCurrentProviderId();
  const requesterProviderId = authorization.requester?.provider?.id;
  const isAuthUpdated = !!authorization.original_service_authorization?.id;
  const isAuthUpdatedByThisProvider = isAuthUpdated && (currentProviderId === requesterProviderId);

  const person = getPersonData(data, authorization, includeInternalNonConsentedClients);

  const isEdition = !!authorization.original_service_authorization?.id;
  const shouldExcludeLineage = hasRequestedOrInReviewEdition(authorization);

  const exclusionFilters = updateEditAuthorizationsFeatureEnabled && shouldExcludeLineage ? {
    original_and_editions: { id: isEdition ? authorization.original_service_authorization.id : authorization.id },
    non_original_from_requested: true,
  } : null;

  const baseParams = {
    feeScheduleProgram: feeScheduleProgram.id,
    person: person.id,
    requestedStartsAt: moment(authorization.requested_starts_at).toISOString(),
    requestedEndsAt: moment(authorization.requested_ends_at).toISOString(),
    enabled: Boolean(feeScheduleProgram.id),
  };

  const { authorizedSpends, meta } = useAuthorizedSpendsWithExclusions(baseParams, exclusionFilters);

  const { authorizedSpends: voidedAuthorizedSpends } = useAuthorizedSpends({
    ...baseParams,
    state: AUTHORIZATION_STATUSES.void,
  });

  const capInfo = getCapInformation(feeScheduleProgram, meta);
  const totalRequestedSpend = meta?.total_requested_spend || 0;

  const isCostBased = ['cost_based_reimbursement', 'funds_distributed'].includes(feeScheduleProgram.payment_type);
  const isDataLoading = isLoading || isMemoLoading || isReferralsMemoLoading;

  const formattedAmountAvailable = capInfo.hasCap && capInfo.formattedAvailableAmount;
  const isAuthAccepted = authorization.state === AUTHORIZATION_STATUSES.accepted;
  const isAuthRejected = authorization.state === AUTHORIZATION_STATUSES.rejected;
  const isAuthInReview = authorization.state === AUTHORIZATION_STATUSES.inReview && inReviewFeatureEnabled;
  const isAuthVoided = authorization.state === AUTHORIZATION_STATUSES.void;
  const shouldShowVoidedCard = updateEditAuthorizationsFeatureEnabled && isAuthVoided && !isAuthUpdatedByThisProvider;

  if (isDataLoading) {
    return <Spinner className="mt-4" />;
  }

  return (
    <div>
      <ServiceAuthorizationDetailsActionBar
        authorization={authorization}
      />
      <div className="p-6 space-y-6">
        {shouldShowVoidedCard && (
          <ServiceAuthorizationVoidedCard authorization={authorization} />
        )}
        {isAuthAccepted && !isAuthUpdatedByThisProvider && (
          <ServiceAuthorizationAcceptedCard authorization={authorization} />
        )}
        {isAuthRejected && (
          <ServiceAuthorizationRejectionCard authorization={authorization} />
        )}
        {isAuthInReview && (
          <ReviewReasonDetailsCard
            reviewReason={authorization.service_authorization_review_reason}
            inReviewNote={authorization.in_review_note}
            assignee={authorization.assignee}
            onSubmit={handleReviewDetailsSubmit}
            assigneeOptions={assigneeOptions}
            reviewReasonOptions={reviewReasonOptions}
          />
        )}
        {(updateEditAuthorizationsFeatureEnabled && isAuthUpdatedByThisProvider ? (
          <UnilaterallyUpdatedAuthorizationCard authorization={authorization} />
        ) : (
          <AuthorizationDetailsCard
            authorization={authorization}
            feeScheduleProgram={feeScheduleProgram}
            dateRequested={dateRequested}
            dateRequestedStartsAt={dateRequestedStartsAt}
            dateRequestedEndsAt={dateRequestedEndsAt}
            isSingleDateRequested={isSingleDateRequested}
            isFspLoaded={isFspLoaded}
            isCostBased={isCostBased}
            previousAuth={previousAuth}
            showOriginalValues={showOriginalValues}
          />
        ))}
        <ClientServiceUtilizationCard
          feeScheduleProgram={feeScheduleProgram}
          serviceAuthorization={authorization}
          authorizedSpends={authorizedSpends}
          voidedAuthorizedSpends={voidedAuthorizedSpends}
          totalRequestedSpend={totalRequestedSpend}
          capInfo={capInfo}
          amountAvailable={formattedAmountAvailable}
        />
        {isEmpty(referralQueries.referrals) ? (
          <CaseDetailsCard
            serviceAuthorization={authorization}
          />
        ) : (
          <ReferralDetailsCard
            referrals={referralQueries.referrals}
            serviceAuthorization={authorization}
          />
        )}
        <ClientDetailsCard
          person={person}
          serviceAuthorization={authorization}
        />
        <AssessmentFormsCard serviceAuthorization={authorization} />
        {serviceAuthTimelineEnabled && (
          <ServiceAuthorizationTimelineEventsCard
            serviceAuthorizationId={authorization.id}
            isAuthVoided={isAuthVoided}
          />
        )}
      </div>
    </div>
  );
};

ServiceAuthorizationDetails.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
};

export default ServiceAuthorizationDetails;
